// Utility functions
import moment from "moment-timezone";
import { convertDateObjectToString, isEpochDate, isIsoDate, minutes2HrsMin } from "src/utils/index";

import { TableCell, Grid, Typography, Tooltip } from "@mui/material"; //TableRow
// Flightchip / Eventchip data types.
import { FlightChip } from "../../Flights/FlightChip";
import { EventChip } from "../../Flights/EventChip";
import { ContentTableCell } from "../../ContentManagement/ContentTableCell";
import { AuditTableCell } from "../../Audit/AuditTableCell";
// Pairing Display components
import { ShortCodeChip } from "../../ShortCodes/ShortCodeChip";
import { PairingPositionCell } from "../CustomCellComponents/PairingPositionCell";
import { PairingEventGroupsCell } from "../CustomCellComponents/PairingEventGroupsCell";
import { ShortDescription } from "src/components/Pairings/ShortDescription";
// Flight Pairing Status components
import { FlightStatusCell } from "src/pages/FlightPairingStatus/FlightStatusCell";
// Booking Display components
import { BookingCard } from "src/pages/CrewTravel/BookingCard";

// Travel Tasks
import { PosFlightChip } from "../../Pairings/PosFlightChip";
import { NonFlightChip } from "../../Pairings/NonFlightChip";
import { CrewMemberChips } from "../../CrewSchedule/CrewMemberChips";
import { ITableColumn } from "./TableHeader";
// Display User Info from provided id
import { ShowUserInfo } from "../../ShowUserInfo/ShowUserInfo";
import { BidPairingState, IForeignSystemReference, IUser } from "@vmosolutions/oc-interfaces";
import { HistoryTableComponentCell, ViewHistoryCell } from "src/pages/History/HistoryTableCells";
import { ManualCheckIn } from "../CustomCellComponents/ManualCheckInCell";
import { SendCheckInReminder } from "../CustomCellComponents/SendCheckInReminder";
import { ErrorStatusCell } from "../CustomCellComponents/ErrorStatusCell";
import { LogicModifierGroupsCell } from "../CustomCellComponents/LogicModifierGroupCell";
// Crew Pairing Change Ledger
import { ManualAcknowledgement } from "src/pages/CrewSchedule/CrewPairingLedger/ManualAcknowledgementCell";
import { DateDisplay, TimeDisplay } from "src/components/DateDisplay";
// Flight Builder Pending Event Status
import { PendingEventStatus } from "src/pages/FlightBuilder/BuildStatus/StatusComponents/PendingEventStatus";
import { PendingPairingStatus } from "src/pages/FlightBuilder/BuildStatus/StatusComponents/PendingPairingStatus";
import { PendingDateInfo } from "src/pages/FlightBuilder/BuildStatus/StatusComponents/PendingDateInfo";
import { PairingCard } from "src/pages/CrewTravel/PairingCard";
// ATA Delay Codes
import { ATACodeChip } from "../CustomCellComponents/ATACodeChip";
import { DelayCodeChip } from "../CustomCellComponents/DelayCodeChip";
// Bid package crew table
import ListAddedIcon from "@mui/icons-material/AddBox";
import ListRemoveIcon from "@mui/icons-material/IndeterminateCheckBox";
import ListAllIcon from "@mui/icons-material/ListAlt";
// Bid package bidpairing table
import { OpenPairingSummary } from "src/components/Pairings/OpenPairingSummary";
import { get } from "lodash";
import { CrewDeviceInfo, CrewDeviceBattery } from "../CustomCellComponents/CrewDevice";

/** Dataset name the vacation bid package list registers with FilterSearchTable. */
export const VACATION_PACKAGE_DATA_TYPE = "vacationBidPackages";

/** Colour per vacation bid package status; anything unlisted renders in the default text colour. */
export const VACATION_PACKAGE_STATUS_COLORS: Record<string, string | undefined> = {
  "Round 1 Open": "Plum",
  "Round 1 Closed": "Plum",
  "Round 2 Open": "Plum",
  "Bidding Closed": "Plum",
  "Awards Published": "MediumSeaGreen",
  "Block Active": "MediumTurquoise",
  "Block Ended": "MediumTurquoise",
  DISABLED: "orange",
};
// Crew Devices

interface IRenderTableData {
  row: any;
  rowIndex: number;
  tableHeadings: ITableColumn[];
  dataType: string;
  isSelected: boolean;
  id?: string; // mosaic window id for components that require it.
  displayAsLocal?: boolean;
}

// Used when display nested values in case they are date types.
function returnBasicTypes(value: string | number, type: string, timezone?: string): string | JSX.Element {
  if (type === "date" && typeof value === "string" && isIsoDate(value).valid) {
    return moment.utc(value).format("ddd, D MMM YYYY HH:mm") + "Z";
  } else if (type === "date" && isEpochDate(value)) {
    return convertDateObjectToString(new Date(value));
  } else if (type === "epochWithTimezone" && typeof value === "number") {
    return <DateDisplay date={value} tz={timezone ?? "Etc/UTC"} />;
  } else {
    return value?.toString();
  }
}

/**
 * @param dataType - Datatype assigned by category selected
 * @param id - Id associated with column
 * @param value - Cell value (string, objects, dates etc...)
 */
function conditionalCellRender(
  dataType: string,
  id: string,
  row: any,
  isSelected: boolean,
  type: string,
  index: number,
  windowId?: string,
  headingValue?: string,
  displayAsLocal?: boolean
): any {
  if (!id || !row) {
    return null;
  }
  const value = get(row, id, undefined); // new safer method to access nested values.
  // Discover if the id indicates a nested values - match only on ] or [

  const charRegex = new RegExp(/[\][]/, "gm");
  const nestedObject = charRegex.exec(id);
  switch (true) {
    // TRAVEL UNBOOKED FLIGHTS
    case dataType === "unbookedFlights" && id === "posflight_chip":
      return (
        <div className="booking_chip">
          <PosFlightChip data={row} />
        </div>
      );
    case dataType === "unbookedFlights" && id === "pairing_id":
      // return <JumpToPairing pairingId={value} />;
      return (
        <Grid item key={`unbooked_flight_associated_pairing_${value}`}>
          <ShortCodeChip code={row?.pairing_shortId} itemId={value} type="pairing" edit={true} />
        </Grid>
      );
    case dataType === "unbookedFlights" && id === "crew":
      return <CrewMemberChips crewInfo={value} direction="row" hideHeader={true} />;

    // TRAVEL BOOKINGS
    case !nestedObject && dataType === "bookings" && id === "component":
      return <BookingCard booking={row} isSelected={isSelected} />;
    case !nestedObject && (dataType === "bookings" || dataType === "bookingErrors") && id === "associatedPairings":
      return (
        <Grid container direction="column" style={{ padding: "2px 5px" }} justifyContent="flex-start">
          <Typography variant="caption">Associated Pairings</Typography>
          {value?.map((pairingInfo: { pairingId: string; shortId: string }) => (
            <Grid item key={`booking_associated_pairing_${pairingInfo?.pairingId}`}>
              <ShortCodeChip code={pairingInfo?.shortId} itemId={pairingInfo?.pairingId} type="pairing" edit={true} />
            </Grid>
          ))}
        </Grid>
      );

    // Booking Errors
    case dataType === "bookingErrors" && id === "pairingComponent":
      return (
        <Grid item key={`unbooked_flight_associated_pairing_${value}`}>
          <PairingCard pairingInfo={row.pairingInfo} isSelected={isSelected} displayAsUTC={!displayAsLocal} />
        </Grid>
      );
    case !nestedObject && dataType === "bookingErrors" && id === "component":
      return (
        <BookingCard
          booking={row}
          isSelected={isSelected}
          displayLocatorOnly={dataType === "bookingErrors"}
          hideUTCToggle={true}
          displayAsLocal={displayAsLocal}
        />
      );

    // AUDITS
    case dataType === "audit":
      return <AuditTableCell data={row} cellId={id} />;

    // Content
    case !nestedObject && dataType === "content":
      return <ContentTableCell data={row} cellId={id} />;

    // History
    case !nestedObject && dataType === "history" && id === "component":
      return <HistoryTableComponentCell data={row} />;
    case !nestedObject && dataType === "history" && id === "modBy":
      return <ShowUserInfo userId={row.modBy} getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`} />;
    case !nestedObject && dataType === "history" && id === "docId":
      return <ViewHistoryCell path={[windowId]} id={value} collection={headingValue} />;
    // PAIRINGS
    case !nestedObject && dataType === "pairings" && id === "_id" && !!row?.shortId && !!row._id:
      return <ShortCodeChip code={row.shortId} itemId={row._id} type="pairing" edit={true} />;
    case !nestedObject && dataType === "pairings" && id === "positions":
      return <PairingPositionCell positions={value} />;
    case !nestedObject && dataType === "pairings" && id === "logicModifiers":
      return <LogicModifierGroupsCell logicModifiers={value} />;
    case !nestedObject && dataType === "pairings" && id === "eventGroups":
      return <PairingEventGroupsCell eventGroups={value} />;
    case !nestedObject && dataType === "pairings" && (id === "createdBy" || id === "modifiedBy"):
      return <ShowUserInfo userId={value} getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`} />;
    case !nestedObject && dataType === "pairings" && id === "shortDescription":
      return <ShortDescription description={value} />;

    // FLIGHTS
    case !nestedObject &&
      (dataType === "dailyFlights" ||
        dataType === "flights" ||
        dataType === "pendingFlights" ||
        dataType === "pendingRecurringFlights") &&
      (id === "flightChip" || id === "_id"): {
      const flightChipData = id === "flightChip" ? row[id] : row;
      return <FlightChip data={flightChipData} />;
    }
    case !nestedObject &&
      (dataType === "dailyFlights" || dataType === "flights") &&
      (id === "createdBy" || id === "modifiedBy"):
      return <ShowUserInfo userId={value} getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`} />;
    case !nestedObject && (dataType === "dailyFlights" || dataType === "flights") && id === "validationStatus": {
      const status: string = row["validation"]
        ? row["validation"]["isValid"] !== undefined && row["validation"]["isValid"] === false
          ? "Invalid"
          : "Valid"
        : "None";
      return <div>{status}</div>;
    }
    case !nestedObject &&
      dataType === "dailyFlights" &&
      (id === "scheduledOut" ||
        id === "scheduledOff" ||
        id === "scheduledOn" ||
        id === "scheduledIn" ||
        id === "actualOut" ||
        id === "actualOff" ||
        id === "actualOn" ||
        id === "actualIn"):
      if (id === "actualOff") {
        const departure = moment.utc(value);
        const shedDep = moment.utc(row["scheduledOff"]);
        const depOffset = moment.duration(departure.diff(shedDep));
        return (
          <Grid container direction="column">
            <Grid item>{moment.utc(value).format("ddd Do H:mm z")}</Grid>
            <Grid item>{`(${minutes2HrsMin(depOffset.asMinutes(), true)})`}</Grid>
          </Grid>
        );
      } else if (id === "actualOn") {
        const arrival = moment.utc(value);
        const shedArrival = moment.utc(row["scheduledOn"]);
        const arrOffset = moment.duration(arrival.diff(shedArrival));
        return (
          <Grid container direction="column">
            <Grid item>{moment.utc(value).format("ddd Do H:mm z")}</Grid>
            <Grid item>{`(${minutes2HrsMin(arrOffset.asMinutes(), true)})`}</Grid>
          </Grid>
        );
      } else {
        return <Grid item>{moment.utc(value).format("ddd Do H:mm z")}</Grid>;
      }
    case !nestedObject &&
      (dataType === "pendingFlights" || dataType === "pendingRecurringFlights") &&
      (id === "scheduledOut" || id === "scheduledIn"):
      return dataType === "pendingRecurringFlights" ? (
        id === "scheduledOut" ? (
          <TimeDisplay time={row["scheduledOut"]} tzICAO={displayAsLocal ? row["origin"] : null}></TimeDisplay>
        ) : (
          <TimeDisplay time={row["scheduledIn"]} tzICAO={displayAsLocal ? row["destination"] : null}></TimeDisplay>
        )
      ) : (
        <DateDisplay
          date={id === "scheduledOut" ? row["scheduledOut"] : row["scheduledIn"]}
          tzICAO={displayAsLocal ? (id === "scheduledOut" ? row["origin"] : row["destination"]) : null}
          showDate={dataType !== "pendingFlights" && dataType === "pendingRecurringFlights" ? false : true}
        />
      );

    // GROUND EVENTS
    case !nestedObject &&
      (dataType === "groundEvents" || dataType === "pendingGroundEvents") &&
      (id === "eventChip" || id === "_id"): {
      const eventChipData = id === "eventChip" ? row[id] : row;
      return <EventChip data={eventChipData} />;
    }
    case !nestedObject && dataType === "groundEvents" && (id === "createdBy" || id === "modifiedBy"):
      return <ShowUserInfo userId={value} getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`} />;
    case !nestedObject && dataType === "pendingGroundEvents" && (id === "scheduledStart" || id === "scheduledEnd"):
      return (
        <DateDisplay
          date={id === "scheduledStart" ? row["scheduledStart"] : row["scheduledEnd"]}
          tzICAO={displayAsLocal ? row["location"] : null}
        />
      );

    // FLIGHT PAIRING STATUS (renders flight status component if column isn't one of the 3 flight info columns.
    case !nestedObject &&
      dataType === "flightPairingStatus" &&
      id !== "flightNumber" &&
      id !== "origin" &&
      id !== "destination":
      return value ? <FlightStatusCell data={value} id={windowId} /> : null;

    // PAIRING CHANGE LEDGER
    case !nestedObject && dataType === "CrewPairingChangeLedger" && id === "crewMember":
      //return <ShowUserInfo userId={value._id} getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`} />;
      return `${value?.firstName ?? ""} ${value?.lastName ?? ""}`;
    case !nestedObject && dataType === "CrewPairingChangeLedger" && id === "event" && value?.type === "pairings":
      return <ShortCodeChip code={value?.shortId} itemId={value?._id} type="pairing" edit={true} />;
    case !nestedObject &&
      dataType === "CrewPairingChangeLedger" &&
      id === "event" &&
      value?.type === "nonFlyingAssignments":
      return <NonFlightChip data={value?.data} />;
    case !nestedObject && dataType === "CrewPairingChangeLedger" && id === "acknowledged":
      return <ManualAcknowledgement pairingLedger={row} />;

    // PAIRING CHECK-IN STATUS
    case !nestedObject && dataType === "PairingCheckInStatus" && (id === "createdBy" || id === "modifiedBy"):
      return <ShowUserInfo userId={value} getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`} />;
    case !nestedObject && dataType === "PairingCheckInStatus" && id === "userCheckedInBy" && value !== "NOT AVAILABLE":
      return <ShowUserInfo userId={value} getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`} />;
    case !nestedObject && dataType === "PairingCheckInStatus" && id === "manualCheckIn":
      return <ManualCheckIn crewId={row.crewId} pairingId={row.pairingId} />;
    case !nestedObject && dataType === "PairingCheckInStatus" && id === "sendReminder":
      return <SendCheckInReminder crewId={row.crewId} pairingId={row.pairingId} />;
    case !nestedObject && dataType === "PairingCheckInStatus" && id === "pairing":
      return value && <ShortCodeChip code={value?.shortId} itemId={value?._id} type="pairing" edit={true} />;
    case dataType === "PairingCheckInStatus" && id === "status":
      return <ErrorStatusCell value={row.status} errorMessage={row.errorMessage} />;
    case dataType === "PairingCheckInStatus" && type === "date":
      return value ? (
        displayAsLocal ? (
          <DateDisplay date={value} tz={row.crewBaseTimezone} />
        ) : (
          <DateDisplay date={value} />
        )
      ) : id === "emailSentAt" || id === "emailedCheckInTime" ? (
        "NOT SENT"
      ) : id === "userCheckedInAt" || id === "userCheckedInFor" ? (
        "NOT CHECKED IN"
      ) : (
        "NOT AVAILABLE"
      );

    // Flight Builder Pending Event Status
    case dataType === "PendingPairingStatus" && id === "pendingEvent":
      return <PendingPairingStatus pairing={row?.pendingEvent} tempShortCode={`P${index + 1}`} />;
    case dataType === "PendingEventStatus" && id === "pendingEvent":
      return <PendingEventStatus {...row?.pendingEvent} />;
    case (dataType === "PendingPairingStatus" || dataType === "PendingEventStatus") && id === "startDate":
      return <PendingDateInfo {...row?.pendingEvent} />;

    // Delay Codes
    case dataType === "delaysDetails" && id === "delay[delayCode]":
      return (
        <Grid container>
          <DelayCodeChip delay={row?.delay} showFullName={false} />
        </Grid>
      );
    case dataType === "delaysDetails" && id === "ATA":
      return value?.length > 0 ? (
        <Grid container>
          {value?.map((ata: any) => (
            <Grid item key={ata?._id}>
              <ATACodeChip flightATA={ata} />
            </Grid>
          ))}
        </Grid>
      ) : null;

    // Bid Package List and crew list tables
    case !nestedObject && dataType === "bidPackages" && id === "positions":
      return <PairingPositionCell positionTitles={value} />;
    case !nestedObject && dataType === "bidPackages" && id === "crewGroups":
      return value?.length ? value?.join(", ") : "";
    case !nestedObject && dataType === "bidPackages" && id === "bidPairingCount":
      return Array.isArray(row?.bidPairings) ? row?.bidPairings?.length : 0;
    case !nestedObject && dataType === "bidPackages" && id === "status":
      switch (value) {
        case "DISABLED":
          return (
            <Typography variant="caption" color="secondary">
              DISABLED
            </Typography>
          );
        case "Block Ended":
          return (
            <Typography variant="caption" style={{ color: "MediumTurquoise" }}>
              Block Ended
            </Typography>
          );
        case "Block Active":
          return (
            <Typography variant="caption" style={{ color: "MediumTurquoise" }}>
              Block Active
            </Typography>
          );
        case "Bidding Closed":
          return (
            <Typography variant="caption" style={{ color: "Plum" }}>
              Bidding Closed
            </Typography>
          );
        case "Bidding Open":
          return (
            <Typography variant="caption" style={{ color: "Plum" }}>
              Bidding Open
            </Typography>
          );
        case "Bidding Pending":
          return <Typography variant="caption">Bidding Pending</Typography>;
        default:
          return <Typography variant="caption">Unknown</Typography>;
      }

    case !!nestedObject && dataType === "bidPackageCrew" && id === "crewMember[crewInfo][isActive]":
      return row?.crewMember?.crewInfo?.isActive === true ? "ACTIVE" : "INACTIVE";
    case !nestedObject && dataType === "bidPackageCrew" && id === "status":
      return (
        <Grid container style={{ width: "auto" }} className="crew_change_buttons" alignItems="center">
          {!row?.crewMember?.crewInfo?.isActive ? (
            <>
              <ListAllIcon className="icon nochange active" style={{ cursor: "default" }} />
              <Typography variant="caption" className="label" style={{ marginLeft: 10 }}>
                Not included in package (no change)
              </Typography>
            </>
          ) : value === "removed" ? (
            <>
              <ListRemoveIcon className="icon removed active" style={{ cursor: "default" }} />
              <Typography variant="caption" className="label" style={{ marginLeft: 10 }}>
                Will be removed from package
              </Typography>
            </>
          ) : value === "added" ? (
            <>
              <ListAddedIcon className="icon added active" style={{ cursor: "default" }} />
              <Typography variant="caption" className="label" style={{ marginLeft: 10 }}>
                Will be added To package
              </Typography>
            </>
          ) : (
            <>
              <ListAllIcon className="icon nochange active" style={{ cursor: "default" }} />
              <Typography variant="caption" className="label" style={{ marginLeft: 10 }}>
                Already In Package (no change)
              </Typography>
            </>
          )}
        </Grid>
      );
    // BID PACKAGE BIDPAIRINGS
    case dataType === "bidPairings" && id === "pairingShortCode":
      return row?.pairing?.displayPairing ? (
        <ShortCodeChip
          code={row?.pairing?.displayPairing?.shortId}
          itemId={row?.pairing?.displayPairing?._id}
          type="pairing"
          edit={false} // Do not allow edits as users are looking at bid pairing "snapshots" which may not be accurate.
        />
      ) : (
        "None"
      );
    case dataType === "bidPairings" && id === "positions":
      return row?.pairing?.displayPairing ? (
        <PairingPositionCell positions={row?.pairing?.displayPairing?.positions} />
      ) : (
        "None"
      );
    case dataType === "bidPairings" && id === "logicModifiers":
      return row?.pairing?.displayPairing ? (
        <LogicModifierGroupsCell logicModifiers={row?.pairing?.displayPairing?.logicModifiers} />
      ) : (
        "None"
      );
    case dataType === "bidPairings" && id === "eventGroups":
      return row?.pairing?.displayPairing ? (
        <PairingEventGroupsCell eventGroups={row?.pairing?.displayPairing?.eventGroups} />
      ) : (
        "None"
      );
    case dataType === "bidPairings" && id === "pairing[pairingSummary]":
      return row?._id && row?.pairing?.displayPairing?.shortId ? (
        <OpenPairingSummary shortId={row?.pairing?.displayPairing?.shortId} pairingId={row?._id} isBidPairing={true} />
      ) : (
        "None"
      );
    case dataType === "bidPairings" && id === "shortDescription":
      return row?.pairing?.shortDescription ? (
        <ShortDescription description={row?.pairing?.shortDescription} />
      ) : (
        "None"
      );
    case dataType === "bidPairings" && id === "state": {
      const pairingState = row?.state as BidPairingState;
      const textColor =
        pairingState === BidPairingState.error || !pairingState
          ? "error"
          : pairingState === BidPairingState.notApplicable
            ? "secondary"
            : pairingState === BidPairingState.pending
              ? "primary"
              : "inherit";
      const bidPairingStatus =
        pairingState === BidPairingState.error
          ? "Error"
          : pairingState === BidPairingState.notApplicable
            ? "Not Applicable"
            : pairingState === BidPairingState.pending
              ? "Pending"
              : pairingState === BidPairingState.populated
                ? "Populated"
                : "Status Unknown";
      return (
        <Tooltip title={row?.stateReason ?? ""} placement="top">
          <span style={{ color: "#00FF00" }}>
            <Typography variant="caption" color={textColor}>
              {bidPairingStatus}
            </Typography>
          </span>
        </Tooltip>
      );
    }

    case dataType === "crewDevices" && id === "device":
      return <CrewDeviceInfo device={value} state={row?.state} />;
    case dataType === "crewDevices" && id === "device[active]":
      return value ? (
        <Typography sx={{ fontSize: "12px" }} color="greenyellow">
          Active
        </Typography>
      ) : (
        <Typography sx={{ fontSize: "12px" }} color="red">
          Inactive
        </Typography>
      );
    case dataType === "crewDevices" && id === "state[batteryState]":
      return <CrewDeviceBattery device={value} state={row?.state} />;
    case dataType === "crewDevices" && id === "state[availableGb]": {
      const capacity = row?.state?.capacityGb;
      return !!value && !!capacity ? (
        <Typography sx={{ fontSize: "12px" }}>{`${capacity} GB (${value} free)`}</Typography>
      ) : (
        "unknown"
      );
    }
    // Crew Rule Category Assignments (Admin Section) - return inifinity symbol if no date set.
    case dataType === "ruleCategoryAssignmentsAssignments" && type === "date":
      return value ? returnBasicTypes(value, type) : "∞";

    // DATES (isoString or epochs)
    case !nestedObject && type === "date":
      return returnBasicTypes(value, type);
    case !nestedObject && type === "epochWithTimezone":
      return returnBasicTypes(value, type, displayAsLocal ? row?.crewBaseTimezone : null);
    case type === "array" && id === "foreignRef" && Array.isArray(value): // && !moment(value).isValid():
      return (value as IForeignSystemReference[]).map((x) => `${x.system}:${x.foreignId}`).join("/");

    // Vacation bid package lifecycle, coloured the same way the status filter menu colours it.
    case dataType === VACATION_PACKAGE_DATA_TYPE && id === "status" && typeof value === "string":
      return (
        <Typography sx={{ fontSize: "12px" }} style={{ color: VACATION_PACKAGE_STATUS_COLORS[value] }}>
          {value}
        </Typography>
      );

    // An array column with no case of its own rendered blank; a list of primitives is readable.
    case type === "array" &&
      Array.isArray(value) &&
      value.every((entry) => entry === null || ["string", "number", "boolean"].includes(typeof entry)):
      return (value as (string | number | boolean)[]).join(", ");

    // NESTED VALUES (support for up to 3 levels of nested objects)
    case !!nestedObject: {
      const indexWithChar: number = id.search(charRegex);
      // this needs to be written as follows otherwise firstDataName is undefined.
      const firstDataName: string = indexWithChar > -1 ? id.substring(0, indexWithChar) : id;
      const secondDataName: string = indexWithChar > -1 ? id.substring(indexWithChar + 1, id.length - 1) : null;

      //case where you have an object 3 objects deep [object1][object2][object3]
      if (secondDataName.includes("][")) {
        const indexOfThirdKey: number = secondDataName.indexOf("][");
        const secondKey = secondDataName.substring(0, indexOfThirdKey);
        const thirdKey = secondDataName.substring(indexOfThirdKey + 2, secondDataName.length);

        if (!!row[firstDataName] && !!row[firstDataName][secondKey] && !!row[firstDataName][secondKey][thirdKey]) {
          return returnBasicTypes(
            row[firstDataName][secondKey][thirdKey],
            type,
            displayAsLocal ? row?.crewBaseTimezone : null
          );
        } else {
          return "None";
        }
      } else {
        const eachItem = row[firstDataName];
        // Checks if the current item is an object within an array or just an object
        // Assumption: that the array will always be size one and so I would only need to get the first item in the array
        if (!!eachItem && eachItem.length > 0 && eachItem[0]) {
          return returnBasicTypes(
            row[firstDataName]["0"][secondDataName],
            type,
            displayAsLocal ? row?.crewBaseTimezone : null
          );
        }
        // If the length is less than 0, then this means that it's just an object or that the type is undefined
        else {
          const noData: boolean = !!row[firstDataName] && !!row[firstDataName][secondDataName] ? false : true;
          if (noData) {
            return id === "aircraft[registration]" ? "Unassigned" : "None";
          } else {
            return returnBasicTypes(
              row[firstDataName][secondDataName],
              type,
              displayAsLocal ? row?.crewBaseTimezone : null
            );
          }
        }
      }
    }

    // return number as string;
    case !nestedObject && type === "number" && typeof value === "number":
      return value?.toString();

    // DEFAULT RETURN
    default:
      return typeof value === "string" ? value : typeof value === "boolean" ? value?.toString() : null;
  }
}

/**
 * Accepts an object and renders the data in table cells into the table corresponding to their appropriate column
 * @param props - IRenderTableData
 */
export const TableCellContent = (props: IRenderTableData): JSX.Element => {
  const { row, rowIndex, tableHeadings, dataType, isSelected, displayAsLocal } = props;
  const cellContent = tableHeadings.map((column: ITableColumn, index: number) => {
    const { id } = column;
    const itemIndex = row?._id ? row._id : index;
    const cellKey = `${id}-${itemIndex}`;
    const cellHeading = tableHeadings.find((heading: any) => heading.id === id);
    // style={{ verticalAlign: "baseline" }} - Rem,oved from Table Cell styling for now as most content looks worse with it.
    return (
      <TableCell scope="row" key={cellKey}>
        <div>
          {conditionalCellRender(
            dataType,
            id,
            row,
            isSelected,
            cellHeading?.type,
            rowIndex,
            props.id,
            cellHeading?.value,
            displayAsLocal
          )}
        </div>
      </TableCell>
    );
  });

  return <>{cellContent}</>;
};
